import { yupResolver } from "@hookform/resolvers/yup";
import { ChatBubbleOutline as ChatBubbleOutlineIcon, Close as CloseIcon } from "@mui/icons-material";
import { Avatar, Button, Dialog, DialogContent, IconButton, Slide, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { forwardRef, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import * as yup from "yup";

import { emailRegex, phoneRegex, validSMS } from "@/config/regex";
import { MEDIA_SIZE, MESSAGE_TYPE } from "@/constants/index.js";
import { getMediaLink } from "@/utils/mediaLink";
import { nameValidationSchema } from "@/utils/validationSchemas";

import { useUrlParams } from "../../../hooks";
import { Email } from "../../../icons";
import { MessageService } from "../../../services";
import { setSimpleValue } from "../../../store/actions";
import AddMessageForm from "../Forms/AddMessageForm";

const useStyles = makeStyles((theme) => ({
  modalPopup: {
    padding: "0",
    position: "relative",
    height: "100%",
  },
  popupHeader: {
    color: "#fff",
    padding: "8px 32px 20px 32px",
    fontSize: "16px",
    fontWeight: 300,
    backgroundColor: "#000",
  },
  popupClose: {
    float: "right",
    color: "#fff",
    "& .MuiSvgIcon-root": {
      fontSize: "24px",
    },
  },
  popupContent: {
    margin: "auto",
    height: "100%",
    padding: "60px 20px 60px",
    width: "400px",
    textAlign: "center",
    display: "flex",
    backgroundColor: "#000",
    flexDirection: "column",
    alignItems: "center",
    "@media (max-width:576px)": {
      maxWidth: "350px",
      width: "100%",
    },
  },
  modalDialog: {
    "& > div > .MuiPaper-root": {
      backgroundColor: "rgba(0,0,0,0.8) !important",
    },
    "& .MuiDialogActions-root": {
      paddingTop: "38px",
      margin: "0 auto",
      maxWidth: "500px",
      paddingLeft: "10px",
      justifyContent: "center",
      "@media (max-width:576px)": {
        bottom: 0,
        position: "absolute",
        width: "100%",
      },
    },
    "& .MuiButton-containedPrimary:hover": {
      backgroundColor: theme.secondaryColor,
    },
    "& .MuiIconButton-root": {
      padding: "0px",
    },
  },
  btnCancel: {
    color: "white",
    padding: "12px 24px",
    marginRight: 10,
    textTransform: "lowercase",
  },
  dialogPaper: {
    backgroundColor: "rgba(0,0,0,.8)",
  },
  button: {
    textTransform: "lowercase",
    bottom: 0,
    right: 0,
    padding: "8px 12px",
    backgroundColor: theme.primaryColor,
    color: "#fff",
    textAlign: "center",
    width: "100%",
    fontWeight: "normal",
    marginTop: "20px",
    borderRadius: 2,
    "@media (max-width:576px)": {
      width: "100%",
    },
  },
  avatar: {
    width: 128,
    height: 128,
    marginBottom: 15,
  },
  disabledButton: {
    color: "rgba(255, 255, 255, .5) !important",
    backgroundColor: `${theme.secondaryColor} !important`,
  },
  typeColor: {
    color: "#fff",
  },
  toast: {
    marginBottom: "0 !important",
    marginTop: "1em !important",
    backgroundRepeat: "no-repeat !important",
    backgroundPosition: "1rem !important",
    backgroundSize: "2rem !important",
  },
  toastBody: {
    textAlign: "left",
  },
  toastProgress: {
    background: "linear-gradient(to right, #97023b, #fff) !important",
  },
}));

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const contactDialogFormSchema = yup.object({
  name: nameValidationSchema,
  phone: yup
    .string()
    .test("required", "Phone is required", (value, values) => {
      const { email } = values.parent;
      return !!(value || email);
    })
    .test("invalid", "Invalid Phone Number", async (value) => {
      if (value === "") return true;
      return phoneRegex.test(value);
    }),
  email: yup
    .string()
    .test("required", "Email is required", (value, values) => {
      const { phone } = values.parent;

      return !!(value || phone);
    })
    .test("invalid", "Invalid Email", async (value) => {
      if (value === "") return true;
      return emailRegex.test(value);
    }),
  message: yup.string().matches(validSMS, "Message contains invalid characters.").required("Message is required."),
});

function ContactDialog(props) {
  const { shareKey, isDialogOpen, setSimpleValue, createdBy = {} } = props;

  const { propertyId, experienceId } = useUrlParams();

  const classes = useStyles({
    ...props,
  });

  const photo = getMediaLink({ size: MEDIA_SIZE.small, link: createdBy?.photoUrl });
  const [createdByPhone = {}] = createdBy?.phones || [];
  const headerRef = useRef();
  const centerRef = useRef();

  const {
    handleSubmit,
    formState: { isDirty, isValid },
    getValues,
    control,
    reset,
    trigger,
  } = useForm({
    mode: "all",
    resolver: yupResolver(contactDialogFormSchema),
    defaultValues: {
      name: "",
      phone: "",
      email: "",
      message: "",
    },
  });

  const values = getValues();

  useEffect(() => {
    trigger().then();
  }, [values.email, values.phone]);

  const onCloseDialog = () => {
    reset();
    setSimpleValue(shareKey, undefined);
  };

  const onContentClick = (e) => {
    if (e.button !== 2 && !headerRef.current.contains(e.target) && !centerRef.current.contains(e.target)) {
      const listener = () => {
        onCloseDialog();
      };
      e.target.addEventListener("mouseup", listener);
    }
  };

  const onSubmit = async (values, event) => {
    const type = event.target.getAttribute("data-type");
    const { name: senderName, message: senderText, email: senderEmail, phone: senderPhone } = values;

    try {
      const data = {
        type,
        senderName,
        senderText,
        ...(senderEmail && { senderEmail }),
        ...(senderPhone && { senderPhone }),
        shareUrl: window.location.href,
      };
      const { err } = await MessageService.addMessage(propertyId, experienceId, data);

      if (!err) {
        toast.success(`Sent ${type} to contact`);
        onCloseDialog();
      }
    } catch {
      toast.error("Error sending to contact", {
        containerId: "contactDialog",
      });
    }
  };

  const disabledButton = (type) => {
    if (type === MESSAGE_TYPE.EMAIL) {
      return !isDirty || !isValid || !values.email;
    }

    return !isDirty || !isValid || !values.phone;
  };

  return (
    <Dialog
      className={classes.modalDialog}
      classes={{ paper: classes.dialogPaper }}
      open={isDialogOpen}
      fullScreen
      fullWidth
      onClose={onCloseDialog}
      aria-labelledby="form-dialog-title"
      TransitionComponent={Transition}
      maxWidth="md"
      data-testid="form-dialog-title"
    >
      <DialogContent className={classes.modalPopup} onMouseDown={onContentClick}>
        <div className={classes.popupHeader} ref={headerRef}>
          Contact
          <span className={classes.popupClose}>
            <IconButton size="small" className={classes.popupClose} onClick={onCloseDialog}>
              <CloseIcon />
            </IconButton>
          </span>
        </div>
        <div className={classes.popupContent} ref={centerRef}>
          <Avatar src={photo} className={classes.avatar} data-testid="form-dialog-avatar" />
          <Typography className={classes.typeColor} variant="h6">
            {createdBy?.name}
          </Typography>
          <Typography className={classes.typeColor} variant="body1">
            {createdByPhone.code || ""}
            {createdByPhone.number || ""}
          </Typography>
          <Typography className={classes.typeColor} variant="body1" gutterBottom>
            {createdBy?.email}
          </Typography>
          <AddMessageForm control={control} />
          <Button
            disabled={disabledButton("text")}
            classes={{
              root: classes.button,
              disabled: classes.disabledButton,
            }}
            variant="contained"
            color="primary"
            fullWidth
            data-type={MESSAGE_TYPE.TEXT}
            onClick={handleSubmit(onSubmit)}
          >
            <ChatBubbleOutlineIcon
              style={{
                marginRight: 8,
                opacity: disabledButton(MESSAGE_TYPE.TEXT) ? 0.5 : 1,
              }}
            />{" "}
            send as text message
          </Button>
          <Button
            disabled={disabledButton("email")}
            style={{
              marginBottom: 20,
            }}
            classes={{
              root: classes.button,
              disabled: classes.disabledButton,
            }}
            variant="contained"
            color="primary"
            fullWidth
            data-type={MESSAGE_TYPE.EMAIL}
            onClick={handleSubmit(onSubmit)}
          >
            <Email
              style={{
                marginRight: 10,
                opacity: disabledButton(MESSAGE_TYPE.EMAIL) ? 0.5 : 1,
              }}
            />
            send as email
          </Button>
          <Button className={classes.btnCancel} onClick={onCloseDialog}>
            cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function mapStateToProps(state, ownProps) {
  const { simpleValues } = state;
  const { documentName } = ownProps;

  const shareKey = `contact_${documentName}`;
  const isDialogOpen = !!simpleValues?.[shareKey];

  return {
    isDialogOpen,
    shareKey,
  };
}

export default connect(mapStateToProps, { setSimpleValue })(ContactDialog);
